import {
  addDaysIso,
  AGREED_DEFAULT_DAYS,
  agreedCatalog,
  agreedStatus,
  bestAgreed,
  fmtNum,
  lei,
  parseNum,
  planReorder,
  siteLabel,
  type AgreedPrice,
  type OrderList,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { DateInput, dotDate } from '../components/DateInput';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import type { Route } from '../routes';

/**
 * Comanda rapidă (shared/reorder.ts): comanzi din nou produsele obișnuite la prețurile
 * convenite cu furnizorii, fără cerere de ofertă. Pentru fiecare produs, Procumi alege
 * cel mai bun preț convenit încă bun; iese câte o comandă pe furnizor, prin aprobare.
 * Trei file: Comandă, Liste, Prețuri convenite.
 */

type Tab = 'order' | 'lists' | 'prices';

/** Prețul pe unitate, cu banii lui: „0,35 lei”, „3.920 lei” (totalurile rămân rotunjite). */
const unitLei = (n: number) => t('{0} lei', fmtNum(Math.round(n * 100) / 100));

const STATUS: Record<ReturnType<typeof agreedStatus>, { cls: string; text: string }> = {
  valid: { cls: 'ok', text: t('Valabil') },
  soon: { cls: 'wait', text: t('Expiră curând') },
  expired: { cls: 'late', text: t('Expirat') },
};

export function ReorderPage({
  go,
  toast,
  tab: firstTab,
}: {
  go: (r: Route) => void;
  toast: Toast;
  tab?: Tab;
}) {
  const data = useAsync(() => api.reorder(), [], 'reorder');
  const sites = useAsync(() => api.sites(), [], 'sites');
  const [tab, setTab] = useState<Tab>(firstTab ?? 'order');

  if (data.loading || sites.loading) return <Loading />;
  if (data.error || !data.data)
    return <ErrorBox error={data.error ?? t('Nu am putut încărca.')} onRetry={data.reload} />;
  const { today, prices, lists } = data.data;
  const setPrices = (p: AgreedPrice[]) => data.setData({ ...data.data!, prices: p });
  const setLists = (l: OrderList[]) => data.setData({ ...data.data!, lists: l });
  const openSites = (sites.data ?? []).filter((s) => !s.closedAt);

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Comandă rapidă')}</h1>
          <p>
            {t(
              'Comanzi din nou la prețurile convenite cu furnizorii, fără cerere de ofertă. Pentru fiecare produs, Procumi alege cel mai bun preț încă valabil și face câte o comandă pe furnizor.',
            )}
          </p>
        </div>
      </div>
      <div className="filters" role="group" aria-label={t('Secțiuni')}>
        {(
          [
            ['order', t('Comandă')],
            ['lists', t('Liste')],
            ['prices', t('Prețuri convenite')],
          ] as const
        ).map(([id, label]) => (
          <button key={id} aria-pressed={tab === id} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'order' ? (
        <OrderTab
          today={today}
          prices={prices}
          lists={lists}
          sites={openSites}
          go={go}
          toast={toast}
          onPrices={() => setTab('prices')}
        />
      ) : tab === 'lists' ? (
        <ListsTab
          today={today}
          prices={prices}
          lists={lists}
          sites={openSites}
          toast={toast}
          onLists={setLists}
        />
      ) : (
        <PricesTab today={today} prices={prices} toast={toast} onPrices={setPrices} />
      )}
    </>
  );
}

type SiteOpt = { id: string; name: string; locality?: string | null };

/* ---------- Comandă ---------- */

function OrderTab({
  today,
  prices,
  lists,
  sites,
  go,
  toast,
  onPrices,
}: {
  today: string;
  prices: AgreedPrice[];
  lists: OrderList[];
  sites: SiteOpt[];
  go: (r: Route) => void;
  toast: Toast;
  onPrices: () => void;
}) {
  const [listId, setListId] = useState(lists[0]?.id ?? '');
  const list = lists.find((l) => l.id === listId) ?? null;
  const [siteId, setSiteId] = useState(list?.siteId ?? sites[0]?.id ?? '');
  const [neededBy, setNeededBy] = useState('');
  const [qty, setQty] = useState<Record<string, string>>(() => qtyOf(list));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Awaited<ReturnType<typeof api.placeReorder>>['orders'] | null>(null);

  const rows = useMemo(() => {
    if (list) return list.items.map((i) => ({ key: i.key, desc: i.desc, unit: i.unit }));
    return agreedCatalog(prices, today).map((p) => ({ key: p.key, desc: p.desc, unit: p.unit }));
  }, [list, prices, today]);
  const lines = rows
    .map((r) => ({ desc: r.desc, unit: r.unit, qty: parseNum(qty[r.key] ?? '') ?? 0 }))
    .filter((l) => l.qty > 0);
  const plan = planReorder(lines, prices, today);
  const total = plan.groups.reduce((n, g) => n + g.total, 0);

  const pickList = (id: string) => {
    setListId(id);
    const l = lists.find((x) => x.id === id) ?? null;
    setQty(qtyOf(l));
    if (l?.siteId) setSiteId(l.siteId);
  };

  const order = async () => {
    if (!siteId) return toast(t('Alege șantierul de livrare.'));
    if (plan.missing.length)
      return toast(t('Fără preț convenit: {0}.', plan.missing.map((m) => m.desc).join(', ')));
    if (!plan.groups.length) return toast(t('Scrie cel puțin o cantitate.'));
    setBusy(true);
    try {
      const out = await api.placeReorder({
        siteId,
        title: list?.name ?? t('Comandă rapidă'),
        neededBy: neededBy || null,
        lines: plan.groups.flatMap((g) => g.lines.map((l) => ({ desc: l.desc, unit: l.unit, qty: l.qty }))),
      });
      setDone(out.orders);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="sheet ro-done">
        <h2>{done.length === 1 ? t('Comanda e făcută') : t('{0} comenzi făcute', done.length)}</h2>
        <ul className="ro-orders">
          {done.map((o) => (
            <li key={o.orderId}>
              <span>
                <b>{o.number}</b> · {o.supplier} · {lei(o.total)}
                {o.waiting ? (
                  <span className="pill wait">{t('așteaptă aprobare')}</span>
                ) : (
                  <span className="pill wait">{t('netrimisă')}</span>
                )}
              </span>
              <button
                className="btn small"
                onClick={() => go({ view: 'order', rfqId: o.rfqId, orderId: o.orderId })}
              >
                {t('Deschide comanda')}
              </button>
            </li>
          ))}
        </ul>
        <p className="hint">
          {t('Din pagina comenzii o trimiți furnizorului și treci livrarea, ca la orice comandă.')}
        </p>
        <button className="btn" onClick={() => setDone(null)}>
          {t('Altă comandă')}
        </button>
      </section>
    );
  }

  if (!prices.some((p) => p.validUntil >= today)) {
    return (
      <section className="sheet ro-empty">
        <h2>{t('Încă nu ai prețuri convenite')}</h2>
        <p>
          {t(
            'Când alegi furnizorul la o cerere de ofertă, apasă „Păstrează prețurile”: prețurile câștigătoare devin prețuri convenite pentru 90 de zile. Sau scrie-le de mână dintr-o listă de prețuri sau un contract.',
          )}
        </p>
        <button className="btn primary" onClick={onPrices}>
          {t('Adaugă prețuri convenite')}
        </button>
      </section>
    );
  }

  return (
    <section className="sheet ro-order">
      <div className="ro-opts">
        <label className="f">
          {t('Lista')}
          <select className="inp" value={listId} onChange={(e) => pickList(e.target.value)}>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
            <option value="">{t('Toate produsele cu preț convenit')}</option>
          </select>
        </label>
        <label className="f">
          {t('Livrare pe șantier')}
          <select className="inp" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {siteLabel({ name: s.name, locality: s.locality ?? '' })}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          {t('Când trebuie (opțional)')}
          <DateInput value={neededBy} min={today} onChange={setNeededBy} />
        </label>
      </div>

      <ul className="ro-rows">
        {rows.map((r) => {
          const best = bestAgreed(prices, r.key, today);
          return (
            <li key={r.key} className={best ? '' : 'none'}>
              <span className="ro-prod">
                <b>{r.desc}</b>
                <small>
                  {best
                    ? `${best.supplier.name} · ${unitLei(best.unitPrice)} / ${r.unit}${best.minQty ? t(' · minim {0}', fmtNum(best.minQty)) : ''}`
                    : t('Fără preț convenit valabil')}
                </small>
              </span>
              <span className="ro-qty">
                <input
                  className="inp"
                  inputMode="decimal"
                  aria-label={t('Cantitate {0}', r.desc)}
                  placeholder="0"
                  value={qty[r.key] ?? ''}
                  onChange={(e) => setQty({ ...qty, [r.key]: e.target.value })}
                />
                <small>{r.unit}</small>
              </span>
            </li>
          );
        })}
      </ul>

      <div className="ro-sum">
        {plan.groups.map((g) => (
          <p key={g.supplierId}>
            {g.lines[0]!.price.supplier.name}: {g.lines.length}{' '}
            {g.lines.length === 1 ? t('produs') : t('produse')} · <b>{lei(g.total)}</b>
            {g.lines.some((l) => l.belowMin) ? (
              <span className="pill wait">{t('sub comanda minimă')}</span>
            ) : null}
          </p>
        ))}
        {plan.missing.length ? (
          <p className="ro-miss">
            {t(
              'Fără preț convenit: {0}. Scoate cantitatea sau cere oferte.',
              plan.missing.map((m) => m.desc).join(', '),
            )}
          </p>
        ) : null}
        <button
          className="btn primary"
          disabled={busy || !plan.groups.length || !!plan.missing.length}
          onClick={() => void order()}
        >
          {plan.groups.length > 1
            ? t('Fă {0} comenzi · {1}', plan.groups.length, lei(total))
            : t('Fă comanda{0}', total ? ` · ${lei(total)}` : '')}
        </button>
        <p className="hint">
          {t('Prețuri fără TVA. Peste pragul de aprobare, comanda așteaptă aprobatorul.')}
        </p>
      </div>
    </section>
  );
}

function qtyOf(list: OrderList | null): Record<string, string> {
  if (!list) return {};
  return Object.fromEntries(list.items.filter((i) => i.qty != null).map((i) => [i.key, fmtNum(i.qty!)]));
}

/* ---------- Liste ---------- */

function ListsTab({
  today,
  prices,
  lists,
  sites,
  toast,
  onLists,
}: {
  today: string;
  prices: AgreedPrice[];
  lists: OrderList[];
  sites: SiteOpt[];
  toast: Toast;
  onLists: (l: OrderList[]) => void;
}) {
  const [editing, setEditing] = useState<OrderList | 'new' | null>(null);
  const catalog = agreedCatalog(prices, today);

  if (editing) {
    return (
      <ListEditor
        list={editing === 'new' ? null : editing}
        catalog={catalog}
        sites={sites}
        toast={toast}
        onSaved={(l) => {
          onLists(l);
          setEditing(null);
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const remove = async (l: OrderList) => {
    try {
      onLists((await api.deleteOrderList(l.id)).lists);
      toast(t('Lista „{0}” a fost ștearsă.', l.name));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  return (
    <section className="sheet ro-lists">
      <div className="ro-lists-head">
        <p className="hint">
          {t(
            'O listă e ce comanzi de obicei pentru un șantier, cu cantitățile obișnuite. La comandă doar le schimbi.',
          )}
        </p>
        <button className="btn primary" disabled={!catalog.length} onClick={() => setEditing('new')}>
          {t('Listă nouă')}
        </button>
      </div>
      {lists.length ? (
        <ul className="ro-list-cards">
          {lists.map((l) => (
            <li key={l.id}>
              <span>
                <b>{l.name}</b>
                <small>
                  {l.items.length} {l.items.length === 1 ? t('produs') : t('produse')}
                  {l.siteId ? ` · ${sites.find((s) => s.id === l.siteId)?.name ?? ''}` : ''}
                </small>
              </span>
              <span className="ro-acts">
                <button className="btn small" onClick={() => setEditing(l)}>
                  {t('Modifică')}
                </button>
                <button className="btn small" onClick={() => void remove(l)}>
                  {t('Șterge')}
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ro-none">
          {catalog.length
            ? t('Nicio listă încă. Fă una cu produsele pe care le comanzi des.')
            : t('Listele se fac din produsele cu preț convenit. Adaugă întâi prețuri convenite.')}
        </p>
      )}
    </section>
  );
}

function ListEditor({
  list,
  catalog,
  sites,
  toast,
  onSaved,
  onCancel,
}: {
  list: OrderList | null;
  catalog: AgreedPrice[];
  sites: SiteOpt[];
  toast: Toast;
  onSaved: (l: OrderList[]) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(list?.name ?? '');
  const [siteId, setSiteId] = useState(list?.siteId ?? '');
  // produsele listei: cele din catalog, plus cele vechi care nu mai au preț
  const [picked, setPicked] = useState<Record<string, string>>(() =>
    Object.fromEntries((list?.items ?? []).map((i) => [i.key, i.qty == null ? '' : fmtNum(i.qty)])),
  );
  const rows = [
    ...catalog.map((p) => ({ key: p.key, desc: p.desc, unit: p.unit })),
    ...(list?.items ?? [])
      .filter((i) => !catalog.some((p) => p.key === i.key))
      .map((i) => ({ key: i.key, desc: i.desc, unit: i.unit })),
  ];
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const items = rows
      .filter((r) => r.key in picked)
      .map((r) => ({ desc: r.desc, unit: r.unit, qty: parseNum(picked[r.key] ?? '') || null }));
    if (!name.trim()) return toast(t('Dă un nume listei.'));
    if (!items.length) return toast(t('Bifează cel puțin un produs.'));
    setBusy(true);
    try {
      onSaved(
        (await api.saveOrderList(list?.id ?? null, { name: name.trim(), siteId: siteId || null, items }))
          .lists,
      );
      toast(t('Lista e salvată.'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sheet ro-editor">
      <h2>{list ? t('Modifică „{0}”', list.name) : t('Listă nouă')}</h2>
      <div className="ro-opts">
        <label className="f">
          {t('Numele listei')}
          <input
            className="inp"
            value={name}
            placeholder={t('ex. Bucătărie – săptămânal')}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="f">
          {t('Șantierul obișnuit')}
          <select className="inp" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">{t('Se alege la comandă')}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ul className="ro-rows">
        {rows.map((r) => {
          const on = r.key in picked;
          return (
            <li key={r.key}>
              <label className="ro-prod ro-check">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = { ...picked };
                    if (on) delete next[r.key];
                    else next[r.key] = '';
                    setPicked(next);
                  }}
                />
                <b>{r.desc}</b>
              </label>
              <span className="ro-qty">
                <input
                  className="inp"
                  inputMode="decimal"
                  placeholder={t('de obicei')}
                  aria-label={t('Cantitatea obișnuită {0}', r.desc)}
                  disabled={!on}
                  value={picked[r.key] ?? ''}
                  onChange={(e) => setPicked({ ...picked, [r.key]: e.target.value })}
                />
                <small>{r.unit}</small>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="ro-acts">
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          {t('Salvează lista')}
        </button>
        <button className="btn" onClick={onCancel}>
          {t('Renunță')}
        </button>
      </div>
    </section>
  );
}

/* ---------- Prețuri convenite ---------- */

interface Row {
  desc: string;
  unit: string;
  price: string;
}

function PricesTab({
  today,
  prices,
  toast,
  onPrices,
}: {
  today: string;
  prices: AgreedPrice[];
  toast: Toast;
  onPrices: (p: AgreedPrice[]) => void;
}) {
  const suppliers = useAsync(() => api.suppliers(), [], 'suppliers');
  const [adding, setAdding] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [validUntil, setValidUntil] = useState(addDaysIso(today, AGREED_DEFAULT_DAYS));
  const [rows, setRows] = useState<Row[]>([{ desc: '', unit: t('buc'), price: '' }]);
  const [busy, setBusy] = useState(false);
  const bySupplier = useMemo(() => {
    const m = new Map<string, AgreedPrice[]>();
    for (const p of prices) m.set(p.supplier.name, [...(m.get(p.supplier.name) ?? []), p]);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ro'));
  }, [prices]);

  const add = async () => {
    const items = rows
      .filter((r) => r.desc.trim())
      .map((r) => ({
        desc: r.desc.trim(),
        unit: r.unit.trim() || t('buc'),
        unitPrice: parseNum(r.price) ?? 0,
      }));
    if (!supplierId) return toast(t('Alege furnizorul.'));
    if (!items.length) return toast(t('Scrie cel puțin un produs.'));
    const bad = items.find((i) => !(i.unitPrice > 0));
    if (bad) return toast(t('Scrie prețul pentru „{0}”.', bad.desc));
    setBusy(true);
    try {
      const out = await api.addAgreedPrices({ supplierId, validUntil, items });
      onPrices(out.prices);
      toast(`${out.saved} ${out.saved === 1 ? t('preț salvat') : t('prețuri salvate')}.`);
      setAdding(false);
      setRows([{ desc: '', unit: t('buc'), price: '' }]);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (p: AgreedPrice) => {
    try {
      onPrices((await api.archiveAgreedPrice(p.id)).prices);
    } catch (e) {
      toast(errMsg(e));
    }
  };

  return (
    <section className="sheet ro-prices">
      <div className="ro-lists-head">
        <p className="hint">
          {t(
            'Prețuri fără TVA, bune până la data lor. Un preț nou de la același furnizor îl înlocuiește pe cel vechi. Când expiră, fă o cerere de ofertă: prețurile câștigătoare se păstrează din nou.',
          )}
        </p>
        {adding ? null : (
          <button className="btn primary" onClick={() => setAdding(true)}>
            {t('Adaugă prețuri')}
          </button>
        )}
      </div>

      {adding ? (
        <div className="ro-add">
          <div className="ro-opts">
            <label className="f">
              {t('Furnizorul')}
              <select className="inp" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">{t('Alege…')}</option>
                {(suppliers.data ?? [])
                  .filter((s) => !s.archivedAt)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="f">
              {t('Bune până la')}
              <DateInput value={validUntil} min={today} onChange={setValidUntil} />
            </label>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="ro-add-row">
              <input
                className="inp"
                placeholder={t('Produsul, cum apare pe comandă')}
                aria-label={t('Produsul')}
                value={r.desc}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, desc: e.target.value } : x)))}
              />
              <input
                className="inp ro-unit"
                aria-label={t('Unitatea')}
                value={r.unit}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)))}
              />
              <input
                className="inp ro-price"
                inputMode="decimal"
                placeholder={t('lei')}
                aria-label={t('Prețul în lei, fără TVA')}
                value={r.price}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))}
              />
            </div>
          ))}
          <div className="ro-acts">
            <button
              className="btn small"
              onClick={() => setRows([...rows, { desc: '', unit: t('buc'), price: '' }])}
            >
              {t('Încă un produs')}
            </button>
            <span style={{ flex: 1 }} />
            <button className="btn" onClick={() => setAdding(false)}>
              {t('Renunță')}
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void add()}>
              {t('Salvează prețurile')}
            </button>
          </div>
        </div>
      ) : null}

      {bySupplier.length ? (
        bySupplier.map(([name, list]) => (
          <div key={name} className="ro-sup">
            <h3>{name}</h3>
            <ul className="ro-rows">
              {list.map((p) => {
                const st = STATUS[agreedStatus(p.validUntil, today)];
                return (
                  <li key={p.id}>
                    <span className="ro-prod">
                      <b>{p.desc}</b>
                      <small>
                        {t(
                          '{0} / {1}{2} · până la {3} · {4}',
                          unitLei(p.unitPrice),
                          p.unit,
                          p.minQty ? t(' · minim {0}', fmtNum(p.minQty)) : '',
                          dotDate(p.validUntil),
                          p.sourceRfqId ? t('din cerere de ofertă') : t('scris de mână'),
                        )}
                      </small>
                    </span>
                    <span className="ro-acts">
                      <span className={`pill ${st.cls}`}>{st.text}</span>
                      <button className="btn small" onClick={() => void archive(p)}>
                        {t('Elimină')}
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      ) : (
        <p className="ro-none">{t('Niciun preț convenit încă.')}</p>
      )}
      <p className="hint">
        {t(
          'Același produs înseamnă aceeași denumire și aceeași unitate (literele mari și diacriticele nu contează).',
        )}
      </p>
    </section>
  );
}
